import { IStringLocalizer } from "../../utilities/IStringLocalizer";
import { BaseOfflineProcessData, OfflineProcessingStageType } from "../Models/OfflineProcesses/OfflineProcessModels";
import { ToxnotServiceClient } from "../ToxnotServiceClient";

// ===============================================================================

/**
 *  Manager that handles the polling of an offline process. It is used whenever a client
 *  needs to know the status of an offline process. doPolling is the method to kick off 
 *  this polling. The Api OfflineProcessGet hit every x seconds returning the current
 *  offline process.
*/
export class OfflineProcessPollingManager<TContext, TResults> {

    // ===============================================================================

    private _operationId: string;
    private _onPoll: (offlineProcessData: BaseOfflineProcessData<TContext, TResults>) => void;
    private _onTotallyComplete: ((offlineProcessData: BaseOfflineProcessData<TContext, TResults>) => void) | null;
    private _onFailed: ((offlineProcessData: BaseOfflineProcessData<TContext, TResults>) => void) | null
    private _serviceClient: ToxnotServiceClient;
    private _localizer: IStringLocalizer;

    // ===============================================================================

    /**
    * Constructs the OfflinePollingProcessManager
    * 
    * @param operationId The id of the offline process that is going to be polled.
    * @param localizer Localizer for current user 
    * @param onPoll A callback that is called every time a polling happens.
    * @param onTotallyComplete An optional callback that is called when an offline process has been read as completed successfully.
    * @param onFailed An optional callback that is called when an offline process has been read as failed.
    */
    constructor(
        operationId: string,
        localizer: IStringLocalizer,
        onPoll: (offlineProcessData: BaseOfflineProcessData<TContext, TResults>) => void,
        onTotallyComplete?: (offlineProcessData: BaseOfflineProcessData<TContext, TResults>) => void,
        onFailed?: (offlineProcessData: BaseOfflineProcessData<TContext, TResults>) => void,
    ) {
        this._operationId = operationId;
        this._onPoll = onPoll;
        this._onTotallyComplete = onTotallyComplete ?? null;
        this._onFailed = onFailed ?? null;

        this._serviceClient = new ToxnotServiceClient();
        this._localizer = localizer;
    }

    // ===============================================================================

    /**
    * Starts the polling process by calling the setInterval() function. The provided 
    * polling function is called every poll. If a offline process has a state of 
    * totally complete or failed, the totally complete or failed callbacks are  
    * executed if they are provided.
    */
    public startPolling = (pollingIntervalInMs: number) => {
        const id = setInterval(
            () => {
                this._serviceClient.offlineProcessDataGet(this._operationId, true).then(
                    apiResult => {
                        if (!apiResult.success || apiResult.data === null) {
                            alert(this._localizer.localizeString("Sorry, there was an error while accessing the database."));
                            clearInterval(id);
                            return;
                        }

                        const offlineProcess = apiResult.data as BaseOfflineProcessData<TContext, TResults>;

                        this._onPoll(offlineProcess);

                        if (offlineProcess.state.processingStage === OfflineProcessingStageType.TotallyComplete ||
                            offlineProcess.state.processingStage === OfflineProcessingStageType.Failed) {

                            this.doProcessCompleteActions(offlineProcess, id);
                        }
                    }
                );
            },
            pollingIntervalInMs
        );
    }

    // ===============================================================================

    /*
    * Calls the correct callbacks if the process has completed or it has failed. 
    * clearInterval() is always called.
    */
    private doProcessCompleteActions(offlineProcessData: BaseOfflineProcessData<TContext, TResults>, timerInveralId: NodeJS.Timeout) {
        clearInterval(timerInveralId);

        if (offlineProcessData.state.processingStage === OfflineProcessingStageType.TotallyComplete) {
            if (this._onTotallyComplete === null) {
                return;
            }

            this._onTotallyComplete(offlineProcessData);
        }

        else {
            if (this._onFailed === null) {
                return;
            }

            this._onFailed(offlineProcessData);
        }
    }

    // ===============================================================================

    /*
    * Returns a localized friendly name for offline process stage type. If no matching stage
    * type found that matches the given one, a generic "working" name is returned instead.
    */
    public static getFriendlyNameForOfflineProcessStage(offlineProcessStage: OfflineProcessingStageType, localizer: IStringLocalizer) {
        const map = {
            [OfflineProcessingStageType.Working]: localizer.localizeString("Working..."),
            [OfflineProcessingStageType.Queued]: localizer.localizeString("Queued"),
            [OfflineProcessingStageType.TotallyComplete]: localizer.localizeString("Completed Successfully"),
            [OfflineProcessingStageType.Failed]: localizer.localizeString("Failed"),
            [OfflineProcessingStageType.PublisingPassports]: localizer.localizeString("Publishing Passports..."),
            [OfflineProcessingStageType.SendingEmails]: localizer.localizeString("Sending Emails..."),
        }

        const name = map[offlineProcessStage];

        if (!name) {
            return map[OfflineProcessingStageType.Working]
        }

        return name;
    }
}